import { useQuery } from '@tanstack/react-query';
import { EquipmentAsset, EquipmentStatus, EquipmentUsage } from '@models/warehouse.model';
import { warehouseService } from '@services/warehouse';

export const EQUIPMENT_ASSETS_QUERY_KEY = ['equipmentAssets'];

export const useEquipmentAssets = () => {
  return useQuery<EquipmentAsset[], Error>({
    queryKey: EQUIPMENT_ASSETS_QUERY_KEY,
    queryFn: async () => {
      const assets = await warehouseService.getEquipmentAssets();
      return assets || [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

export const useEquipmentAssetsByStatus = (status: EquipmentStatus) => {
  return useQuery<EquipmentAsset[], Error>({
    queryKey: [...EQUIPMENT_ASSETS_QUERY_KEY, 'byStatus', status],
    queryFn: async () => {
      const assets = await warehouseService.getEquipmentAssetsByStatus(status);
      return assets || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useAvailableEquipment = () => {
  return useQuery<EquipmentAsset[], Error>({
    queryKey: [...EQUIPMENT_ASSETS_QUERY_KEY, 'available'],
    queryFn: async () => {
      const assets = await warehouseService.getAvailableEquipment();
      return assets || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useEquipmentByTask = (taskId: string | undefined) => {
  return useQuery<EquipmentAsset[], Error>({
    queryKey: [...EQUIPMENT_ASSETS_QUERY_KEY, 'byTask', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('taskId is required');
      const assets = await warehouseService.getEquipmentByTask(taskId);
      return assets || [];
    },
    enabled: !!taskId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useEquipmentAsset = (assetId: string | undefined) => {
  return useQuery<EquipmentAsset, Error>({
    queryKey: [...EQUIPMENT_ASSETS_QUERY_KEY, assetId],
    queryFn: async () => {
      if (!assetId) throw new Error('assetId is required');
      return await warehouseService.getEquipmentAsset(assetId);
    },
    enabled: !!assetId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useEquipmentUsageHistory = (assetId: string | undefined) => {
  return useQuery<EquipmentUsage[], Error>({
    queryKey: [...EQUIPMENT_ASSETS_QUERY_KEY, assetId, 'usageHistory'],
    queryFn: async () => {
      if (!assetId) throw new Error('assetId is required');
      const history = await warehouseService.getEquipmentUsageHistory(assetId);
      return history || [];
    },
    enabled: !!assetId,
    staleTime: 2 * 60 * 1000,
  });
};

export default useEquipmentAssets;
